'use client';

import { useState } from 'react';
import Image from 'next/image';
import type { BoardMember } from './interfaces';
import { getInitials } from './utils/getInitials';

/**
 * @group Public
 * @summary Board member card component
 * 
 * Displays an individual board member profile with headshot,
 * name, and title in a styled card layout.
 * The entire card is clickable to expand the detail panel at the top of the page.
 */
export function BoardMemberCard({
  member,
  isExpanded = false,
  hasAnyExpanded = false,
  onExpand,
}: {
  member: BoardMember;
  isExpanded?: boolean;
  hasAnyExpanded?: boolean;
  onExpand?: () => void;
}) {
  const [imageError, setImageError] = useState(false);
  const initialsResult = getInitials(member);
  const initials = initialsResult.match({
    ok: (value) => value,
    err: () => '?',
  });

  return (
    <div
      className={`
        bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden
        transition-all duration-300 ease-in-out cursor-pointer flex-shrink-0
        ${isExpanded ? 'ring-2 ring-emerald-500 shadow-md opacity-100' : hasAnyExpanded ? 'opacity-65 hover:shadow-md hover:opacity-80 w-full' : 'hover:shadow-md'}
      `}
      onClick={onExpand}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onExpand?.();
        }
        if (e.key === 'Escape' && isExpanded) {
          e.preventDefault();
          onExpand?.();
        }
      }}
      aria-expanded={isExpanded}
    >
      {/* Headshot Image */}
      <div className={`relative bg-gradient-to-br from-emerald-100 to-emerald-200 overflow-hidden ${hasAnyExpanded && !isExpanded ? 'aspect-[3/4]' : 'aspect-[4/5]'}`}>
        {!imageError ? (
          <Image
            src={member.imageUrl}
            alt={`${member.name} - ${member.role}`}
            fill
            className="object-cover object-top"
            sizes={hasAnyExpanded && !isExpanded ? "280px" : "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"}
            quality={85}
            priority={false}
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className={`font-bold text-emerald-700 opacity-80 transition-all duration-300 ${hasAnyExpanded && !isExpanded ? 'text-2xl' : 'text-4xl sm:text-5xl lg:text-6xl'}`}>
              {initials}
            </div>
          </div>
        )}
      </div>
      
      {/* Member Info */}
      <div className={`transition-all duration-300 ${hasAnyExpanded && !isExpanded ? 'p-3' : 'p-4 sm:p-5 lg:p-6'}`}>
        <h3 className={`font-bold text-gray-900 mb-1 transition-all duration-300 ${hasAnyExpanded && !isExpanded ? 'text-sm' : 'text-base sm:text-lg lg:text-xl'}`}>
          {member.name}
        </h3>
        <p className={`text-emerald-600 font-semibold transition-all duration-300 ${hasAnyExpanded && !isExpanded ? 'text-[10px]' : 'text-xs sm:text-sm lg:text-base'}`}>
          {member.job_title}
        </p>
      </div>
    </div>
  );
}
